import { Button, Image } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useFocusTrap } from '@mantine/hooks';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  authLogin,
  AuthTokenResponse,
  googleVerify,
  GoogleVerifyResponse,
  isMfaRequired,
  MfaRequiredResponse,
  parseApiErrorCode,
} from '@mygpt/app';
import { useLogin } from '@/hooks/useLogin';
import { TokenInfo } from '@/types/interface';
import { removeSearchParam, saveTokenInfo } from '@/utils/tool';
import { GoogleButton } from './GoogleButton';
import { getGoogleLoginState } from './googleLoginState';
import {
  getWidgetLoginContext,
  isWidgetAuthResult,
  WIDGET_AUTH_MESSAGE_TYPE,
  withWidgetLoginContext,
} from '@mygpt/app/apis/widgetLogin';
import MfaVerification from './MfaVerification';
import logo from '@/assets/logo.png';

dayjs.extend(utc);
dayjs.extend(timezone);

/** locked_until 为 ISO8601(UTC)，展示时转 Asia/Tokyo */
const formatTokyoTime = (isoTime?: string) => {
  if (!isoTime) return '';
  return dayjs(isoTime).tz('Asia/Tokyo').format('YYYY-MM-DD HH:mm');
};

/** MFA 挑战上下文：从 authorize / google_verify 的 mfa_required 分支进入 */
interface MfaChallenge {
  mfaToken: string;
  emailMasked: string;
  resendAfter: number;
  /** 验证成功后要回跳的地址（密码流来自 redirect_uri，Google 流来自 state） */
  redirect?: string;
}

const parseRedirectURI = (uri: string | null) => {
  if (!uri) return;

  try {
    return atob(uri);
  } catch (error) {
    console.error(`无效的跳转URL：${uri}`);
    return;
  }
};

// 取根域（末两段），用于判定是否同一站点的子域
const getBaseDomain = (hostname: string) => hostname.toLowerCase().split('.').slice(-2).join('.');

// 校验 redirect URI 是否可信，防止开放重定向攻击。
// 用 URL 解析而非字符串前缀匹配，堵死 //evil.com、gbase.ai.evil.com、gbase.ai@evil.com、/\evil.com 等绕过。
// 放行：同源、相对路径、以及同一根域下的子域（如 gbase.ai ↔ admin.gbase.ai 互跳；onprem 自适应客户域名）。
const validateRedirectURI = (uri: string | undefined): boolean => {
  if (!uri) return false;
  try {
    // 相对路径会基于当前 origin 解析，其 hostname 即当前站点
    const target = new URL(uri, window.location.origin);
    // 只允许 http/https，挡掉 javascript:/data: 等伪协议
    if (target.protocol !== 'http:' && target.protocol !== 'https:') return false;
    const targetHost = target.hostname.toLowerCase();
    const currentHost = window.location.hostname.toLowerCase();
    // 同源（含相对路径）直接放行
    if (targetHost === currentHost) return true;
    // 同一根域下的子域互跳放行
    return getBaseDomain(targetHost) === getBaseDomain(currentHost);
  } catch {
    return false;
  }
};

const toTokenInfo = (data?: GoogleVerifyResponse): TokenInfo | null => {
  if (!data) return null;
  const loginToken = data.loginToken;
  if (loginToken?.accessToken && loginToken?.tokenType && loginToken?.accessTokenExpiresIn) {
    return {
      access_token: loginToken.accessToken,
      refresh_token: loginToken.refreshToken ?? '',
      expires_in: loginToken.accessTokenExpiresIn,
      token_type: loginToken.tokenType,
      scope: '',
    };
  }

  if (data.access_token && data.token_type && data.expires_in) {
    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token ?? '',
      expires_in: Number(data.expires_in),
      token_type: data.token_type,
      scope: data.scope ?? '',
    };
  }

  return null;
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { scheduleTokenRenewal } = useLogin();
  const focusTrapRef = useFocusTrap();
  const { t } = useTranslation();
  const [loginError, setLoginError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const params = new URLSearchParams(location.search);
  const redirectURI = parseRedirectURI(params.get('redirect_uri'));
  // Google 只回传 code/state；从 state 恢复小部件通信上下文（MFA 阶段也继续保留）。
  const googleReturnUrl = parseRedirectURI(params.get('state'));
  const widgetContext =
    getWidgetLoginContext(window.location.href, window.location.origin) ||
    (googleReturnUrl ? getWidgetLoginContext(googleReturnUrl, window.location.origin) : null);
  const isWidgetFlow = !!widgetContext || params.has('widget_login');
  const googlePopupRef = useRef<Window | null>(null);
  const googlePendingRef = useRef(false);
  const closeTimerRef = useRef<number | undefined>(undefined);
  const [googleFallbackUrl, setGoogleFallbackUrl] = useState('');

  const deliverWidgetToken = (token: string) => {
    if (!widgetContext) {
      setLoginError(t('widget.login_callback.failed'));
      return;
    }
    const target = widgetContext.mode === 'inline' ? window.parent : window.opener;
    if (!target || target === window || target.closed) {
      setLoginError(t('widget.login_callback.failed'));
      return;
    }
    target.postMessage(
      { type: WIDGET_AUTH_MESSAGE_TYPE, requestId: widgetContext.requestId, token },
      widgetContext.origin
    );
    if (widgetContext.mode === 'google') closeTimerRef.current = window.setTimeout(() => window.close(), 800);
  };

  useEffect(() => {
    if (widgetContext?.mode !== 'inline') return;
    const receive = (event: MessageEvent) => {
      if (
        !googlePendingRef.current ||
        event.origin !== window.location.origin ||
        !event.source ||
        (googlePopupRef.current && event.source !== googlePopupRef.current) ||
        !isWidgetAuthResult(event.data, widgetContext.requestId)
      ) {
        return;
      }
      googlePendingRef.current = false;
      googlePopupRef.current?.close();
      deliverWidgetToken(event.data.token);
    };
    window.addEventListener('message', receive);
    return () => window.removeEventListener('message', receive);
  }, [widgetContext?.mode, widgetContext?.origin, widgetContext?.requestId]);

  useEffect(
    () => () => {
      googlePopupRef.current?.close();
      window.clearTimeout(closeTimerRef.current);
    },
    []
  );

  const form = useForm({
    initialValues: {
      account: '',
      password: '',
    },

    validate: {
      account: (val) => (val.trim() ? null : t('login_account_required')),
      password: (val) => (val.length < 8 ? t('login_password_min_length') : null),
    },

    transformValues: (values) => ({
      account: values.account.trim(),
      password: values.password.trim(),
    }),
  });

  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);

  /** 保存 token 并调度续期（authorize / mfa verify 成功响应同构，复用同一逻辑） */
  const applyAuthResponse = (data: AuthTokenResponse) => {
    if (isWidgetFlow) {
      const tokenInfo = toTokenInfo(data);
      if (!tokenInfo) {
        setLoginError(t('widget.login_callback.failed'));
        return;
      }
      // iframe 内不依赖共享 cookie，直接交给原小部件保存登录态。
      deliverWidgetToken(`${tokenInfo.token_type} ${tokenInfo.access_token}`);
      return;
    }
    // 检查是否是新接口返回格式（带 loginToken）
    if (data.loginToken) {
      // 新接口处理
      const { loginToken } = data;
      const tokenInfo: TokenInfo = {
        access_token: loginToken.accessToken,
        refresh_token: loginToken.refreshToken,
        expires_in: loginToken.accessTokenExpiresIn,
        token_type: loginToken.tokenType,
        scope: '', // 新接口无scope
      };
      saveTokenInfo(tokenInfo);
      scheduleTokenRenewal();
    } else {
      // /oauth/authorize
      const { roles, ...tokenInfo } = data;
      saveTokenInfo(tokenInfo as TokenInfo);
      scheduleTokenRenewal();
    }
  };

  /** 登录完成后的跳转（仅限同源/同根域） */
  const redirectAfterLogin = (redirect?: string) => {
    if (isWidgetFlow) return;
    console.log('redirectURI', redirect);
    if (redirect && validateRedirectURI(redirect)) {
      // 登录成功后跳转回之前的链接（仅限同源）
      window.location.href = redirect;
    } else {
      navigate('/', { replace: true });
    }
  };

  /** 进入 MFA 验证码步骤（验证码已随 authorize/google_verify 响应自动发出） */
  const startMfaChallenge = (data: MfaRequiredResponse, redirect?: string) => {
    setLoginError('');
    setMfaChallenge({
      mfaToken: data.mfa_token,
      emailMasked: data.email_masked,
      resendAfter: data.resend_after,
      redirect,
    });
  };

  /** MFA 验证成功：响应与 authorize 成功同构，等价执行登录完成逻辑 */
  const handleMfaSuccess = (data: AuthTokenResponse) => {
    const redirect = mfaChallenge?.redirect;
    setMfaChallenge(null);
    applyAuthResponse(data);
    redirectAfterLogin(redirect);
  };

  /** MFA 挑战会话过期：回登录第一步并展示提示 */
  const handleMfaSessionExpired = (message: string) => {
    setMfaChallenge(null);
    setLoginError(message);
  };

  const submit = async (values: { account: string; password: string }, event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    setLoginError(''); // 清除之前的错误信息
    setIsLoading(true); // 开始loading

    try {
      const res = await authLogin({ email: values.account.toLocaleLowerCase(), password: values.password });
      if (!res.data) return;

      // 密码正确但需要 MFA：进入验证码输入步骤（HTTP 200 分支）
      if (isMfaRequired(res.data)) {
        startMfaChallenge(res.data, redirectURI);
        return;
      }

      applyAuthResponse(res.data);
      redirectAfterLogin(redirectURI);
    } catch (error: any) {
      const codeDetail = parseApiErrorCode(error);
      if (codeDetail?.code === 'ACCOUNT_LOCKED') {
        // 账户已锁定：locked_until(ISO8601 UTC) 转 Asia/Tokyo 展示
        setLoginError(t('mfa.account_locked', { time: formatTokyoTime(codeDetail.locked_until) }));
      } else if (codeDetail?.code === 'PASSWORD_EXPIRED') {
        // 密码已过期：引导走「忘记密码」邮箱重置流（重置成功会自动解锁）
        setLoginError(t('mfa.password_expired'));
      } else {
        setLoginError(t('login_invalid_credentials'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const accountInputProps = form.getInputProps('account', { withError: false });
  const passwordInputProps = form.getInputProps('password', { withError: false });

  const welcomeBackText = t('login.welcome_back');
  const dontHaveAccountText = t('login.dont_have_account');
  const signUpText = t('login.sign_up');
  const forgotPasswordText = t('login.forgot_password');
  const continueWithGoogleText = t('login.continue_with_google');

  const googleClientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID ?? '';
  const googleAuthBaseUrl = import.meta.env.VITE_GOOGLE_AUTH_BASE_URL ?? '';

  useEffect(() => {
    // 处理取消Google登录的错误展示
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('error')) {
      setLoginError(t('login_error_google_failed'));
      removeSearchParam('error');
    }
  }, [location.search, t]);

  useEffect(() => {
    // 处理Google认证后跳转回来的场景
    // 调用后端接口获取token
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code) return;

    const run = async () => {
      try {
        const res = await googleVerify({ code, redirectURI: `${window.location.origin}/auth/login` });
        const resData = res?.data;
        // Google 登录同样可能返回 mfa_required 分支，处理方式与密码登录完全一致
        if (resData && isMfaRequired(resData)) {
          startMfaChallenge(resData, state ? parseRedirectURI(state) : undefined);
          return;
        }
        const data: GoogleVerifyResponse | undefined = resData;
        const tokenInfo = toTokenInfo(data);
        if (!tokenInfo) {
          throw new Error(t('login_error_google_failed'));
        }
        if (isWidgetFlow) {
          deliverWidgetToken(`${tokenInfo.token_type} ${tokenInfo.access_token}`);
          return;
        }
        saveTokenInfo(tokenInfo);
        scheduleTokenRenewal();

        if (state) {
          // 如果有跳转url，就跳转回去（仅限同源）
          const url = parseRedirectURI(state);
          if (url && validateRedirectURI(url)) {
            window.location.href = url;
          } else {
            navigate('/', { replace: true });
          }
        } else {
          // 没有的话，跳转到首页
          navigate('/', { replace: true });
        }
      } catch (error: any) {
        console.error(`Google verify error, ${error}`);
        if (error?.statusCode === 403 && error?.errorMsg) {
          setLoginError(error.errorMsg);
        } else {
          setLoginError(t('login_error_google_failed'));
        }
      } finally {
        removeSearchParam('code');
      }
    };
    run();
  }, [location.search, t]);

  // Google OAuth 重定向需要由前端拼接参数跳转至 Google 授权页
  const handleGoogleLogin = () => {
    if (!googleClientId || !googleAuthBaseUrl) return;

    const isInline = widgetContext?.mode === 'inline';
    const returnUrl = widgetContext
      ? withWidgetLoginContext(`${window.location.origin}/auth/login`, {
          ...widgetContext,
          mode: 'google',
          origin: window.location.origin,
        })
      : window.location.href;
    const state = widgetContext ? btoa(returnUrl) : getGoogleLoginState(returnUrl, redirectURI);

    const params = new URLSearchParams({
      client_id: googleClientId,
      redirect_uri: `${window.location.origin}/auth/login`,
      scope: [
        'openid',
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile',
      ].join(' '),
      response_type: 'code',
      access_type: 'offline',
      prompt: 'consent',
      state,
    });
    const googleUrl = `${googleAuthBaseUrl}?${params.toString()}`;
    if (isInline) {
      googlePendingRef.current = true;
      googlePopupRef.current?.close();
      googlePopupRef.current = window.open(googleUrl, '_blank', 'popup=yes,width=480,height=680');
      setGoogleFallbackUrl(googlePopupRef.current ? '' : googleUrl);
    } else {
      // 独立登录页继续在当前标签页授权，不改变原有操作方式。
      window.location.href = googleUrl;
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-gray-50 py-4 px-4 md:py-12 md:px-6 lg:px-8 relative">
      <div className="absolute top-4 right-4 md:top-6 md:right-6 z-10">{/*<LanguageSelector variant="button" />*/}</div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Image src={logo} className="mx-auto h-10 w-auto md:h-12" fit="contain" />

        {!mfaChallenge && (
          <>
            <h2 className="mt-4 md:mt-6 text-center text-2xl md:text-3xl font-bold tracking-tight text-gray-900">
              {welcomeBackText}
            </h2>
            {dontHaveAccountText && (
              <p className="mt-2 text-center text-sm text-gray-600">
                {dontHaveAccountText}{' '}
                <Link
                  to="/auth/register"
                  target={isWidgetFlow ? '_blank' : undefined}
                  rel={isWidgetFlow ? 'noopener noreferrer' : undefined}
                  className="font-semibold text-primary-700 hover:text-primary-600"
                >
                  {signUpText}
                </Link>
              </p>
            )}
          </>
        )}
      </div>

      {mfaChallenge && (
        <div className="mt-6 md:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <MfaVerification
            key={mfaChallenge.mfaToken}
            mfaToken={mfaChallenge.mfaToken}
            emailMasked={mfaChallenge.emailMasked}
            resendAfter={mfaChallenge.resendAfter}
            onSuccess={handleMfaSuccess}
            onBack={() => setMfaChallenge(null)}
            onSessionExpired={handleMfaSessionExpired}
          />
        </div>
      )}

      {!mfaChallenge && (
        <div className="mt-6 md:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <form
            className="space-y-6 bg-white py-6 px-4 md:py-8 md:px-6 shadow rounded-xl border border-gray-200 sm:px-10"
            onSubmit={form.onSubmit(submit)}
            ref={focusTrapRef}
          >
            {loginError && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
                  <p className="ml-3 text-sm text-red-700">{loginError}</p>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="account" className="block text-sm font-medium text-gray-700 mb-1">
                {t('login_account')}
              </label>
              <input
                id="account"
                type="text"
                autoComplete="username"
                placeholder={t('login_account_placeholder')}
                className="block w-full rounded-xl border border-gray-300 px-4 py-3 md:py-3 text-gray-900 placeholder-gray-400 bg-gray-50 hover:border-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:bg-white transition-all duration-200 text-base md:text-sm"
                {...accountInputProps}
              />
              {form.errors.account && <p className="mt-2 text-sm text-red-600">{form.errors.account}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                {t('login_password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder={t('login_password_placeholder')}
                  className="block w-full rounded-xl border border-gray-300 px-4 py-3 md:py-3 text-gray-900 placeholder-gray-400 bg-gray-50 hover:border-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:bg-white transition-all duration-200 text-base md:text-sm"
                  {...passwordInputProps}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4"
                  aria-label={showPassword ? t('login.hide_password') : t('login.show_password')}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {form.errors.password && <p className="mt-2 text-sm text-red-600">{form.errors.password}</p>}
            </div>

            <div className="text-right text-sm">
              <Link
                to="/auth/forgot-password"
                target={isWidgetFlow ? '_blank' : undefined}
                rel={isWidgetFlow ? 'noopener noreferrer' : undefined}
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                {forgotPasswordText}
              </Link>
            </div>

            <Button
              type="submit"
              fullWidth
              size="md"
              radius="md"
              loading={isLoading}
              loaderProps={{ size: 'sm', color: 'white' }}
              className="bg-gray-900 hover:bg-gray-800 text-white font-medium text-base md:text-sm py-3.5 md:py-3 shadow-sm transition-colors duration-200"
              disabled={isLoading}
            >
              {t('login_login')}
            </Button>
            <GoogleButton fullWidth size="md" radius="md" type="button" onClick={handleGoogleLogin}>
              {continueWithGoogleText}
            </GoogleButton>
            {googleFallbackUrl && (
              <p className="text-xs text-gray-500">
                {t('errors.detail.widget_login_popup_blocked')}{' '}
                <a href={googleFallbackUrl} target="_blank" rel="opener" className="underline text-primary-600">
                  {t('widget.login_in_new_tab')}
                </a>
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  );
};

export default Login;
